import { useMemo } from "react";
import { queriesPayPeriods } from "@query";

type PayPeriodInfo = {
  /** Needed to scope work-log reads to this period (`filter[payPeriodId]`). */
  id?: string;
  startDate: Date;
  endDate: Date;
  daysLeftToReview: number;
};

type NextPayPeriodInfo = {
  id?: string;
  startDate: Date;
  endDate: Date;
  daysLeftToOpen: number;
};

type UseCurrentPayPeriodResult = {
  currentPayPeriod?: PayPeriodInfo;
  nextPayPeriod?: NextPayPeriodInfo;
  isLoading: boolean;
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const daysBetween = (from: Date, to: Date): number =>
  Math.max(0, Math.ceil((to.getTime() - from.getTime()) / MS_PER_DAY));

/**
 * Derives the current and next pay period from the v3 pay periods endpoint.
 *
 * v1 had no pay-period endpoint and derived this from the payroll schedule; v3
 * exposes pay periods directly, so we list the periods still open (endDate in
 * the future) and pick the active one plus the following one.
 */
export function useCurrentPayPeriod(): UseCurrentPayPeriodResult {
  // Anchor "now" to the start of today so the query key stays stable across
  // renders (a fresh `new Date()` each render would thrash the cache).
  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const queryPayPeriods = queriesPayPeriods.listPayPeriods.useQuery({
    filter: { endDate: { gte: today } },
    page: { size: 20 }
  });

  const result = useMemo((): Omit<UseCurrentPayPeriodResult, "isLoading"> => {
    const periods = (queryPayPeriods.data?.data ?? [])
      .filter(period => !!period.startDate && !!period.endDate)
      .sort(
        (a, b) =>
          new Date(a.startDate!).getTime() - new Date(b.startDate!).getTime()
      );

    const now = new Date();

    const current = periods.find(
      period =>
        new Date(period.startDate!) <= now && now <= new Date(period.endDate!)
    );
    const next = periods.find(period => new Date(period.startDate!) > now);

    const currentPayPeriod: PayPeriodInfo | undefined = current
      ? {
          id: current.id,
          startDate: new Date(current.startDate!),
          endDate: new Date(current.endDate!),
          daysLeftToReview: daysBetween(now, new Date(current.endDate!))
        }
      : undefined;

    const nextPayPeriod: NextPayPeriodInfo | undefined = next
      ? {
          id: next.id,
          startDate: new Date(next.startDate!),
          endDate: new Date(next.endDate!),
          daysLeftToOpen: daysBetween(now, new Date(next.startDate!))
        }
      : undefined;

    return { currentPayPeriod, nextPayPeriod };
  }, [queryPayPeriods.data]);

  return {
    ...result,
    isLoading: queryPayPeriods.isLoading
  };
}
